import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, Search, User, Ship } from 'lucide-react';
import BoatHullVisualization, { SeatData } from '../shared/BoatHullVisualization';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionRoster } from '../../../hooks/useSessionRoster';
import { useCoachRoster, CoachRosterRower } from '../../../hooks/useCoachRoster';
import { useCoachTeams } from '../../../hooks/useCoachTeams';
import { useAuth } from '../../../contexts/AuthContext';
import { useRosterManagement } from '../../../hooks/useRosterManagement';
import {
  SessionRosterEntry,
  SessionRosterChange,
  telemetryService,
} from '../../../services/telemetry/telemetryService';
import { Button } from '../../ui/Button';
import { useTeamBoats } from '../../../hooks/useTeamBoats';
import { useBoatRoster } from '../../../hooks/useBoatRoster';
import CreateTeamPrompt from '../team/CreateTeamPrompt';

interface RosterMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionName?: string;
  /** Demo sessions are read-only. */
  isDemoSession?: boolean;
  /** When provided (e.g. from a team's Sessions tab), roster is locked to this team and dropdown is hidden. */
  teamId?: string;
  onSave?: () => void;
}

const RosterMappingModal: React.FC<RosterMappingModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  sessionName,
  isDemoSession = false,
  teamId: teamIdFromParent,
  onSave,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: sessionRoster = [], refetch } = useSessionRoster(sessionId);
  const { data: coachTeams = [], isLoading: isLoadingTeams } = useCoachTeams();

  /** Non-demo teams only — same filter as the team select (demo teams are not mappable here). */
  const mappableTeams = useMemo(
    () => coachTeams.filter((m) => m?.team?.id && !m.team.is_demo),
    [coachTeams]
  );
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [sessionTeamId, setSessionTeamId] = useState<string | null>(null);
  /** When opened from master sessions, true after session team has been loaded (avoids flicker). */
  const [sessionTeamLoaded, setSessionTeamLoaded] = useState(false);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
  const [startOverLoading, setStartOverLoading] = useState(false);
  const [showClearSeatsConfirm, setShowClearSeatsConfirm] = useState(false);
  const [clearSeatsLoading, setClearSeatsLoading] = useState(false);
  /** When in team context: use that team. When master sessions + mapping started: lock to session team. Else: use dropdown. */
  const effectiveTeamId =
    teamIdFromParent ??
    (sessionTeamLoaded && sessionTeamId ? sessionTeamId : selectedTeamId || null);
  const { roster: masterRoster, loading: masterRosterLoading } = useCoachRoster(effectiveTeamId);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const prevIsOpenRef = useRef<boolean>(false);

  // Boat quick-fill state
  const [selectedBoatId, setSelectedBoatId] = useState<string>('');
  const [appliedBoatId, setAppliedBoatId] = useState<string | null>(null);
  const { data: teamBoats = [] } = useTeamBoats(effectiveTeamId);
  const { data: boatRoster = [] } = useBoatRoster(selectedBoatId || null);
  const [applyingBoat, setApplyingBoat] = useState(false);
  const isReadOnlyDemo = isDemoSession;

  const {
    draftRoster,
    saving,
    error,
    successMessage,
    rosterOptionsMap,
    updateRosterEntry,
    unassignRower,
    saveChanges,
    setError,
    setSuccessMessage,
  } = useRosterManagement({
    rosterData: sessionRoster,
    masterRoster,
    sessionId,
    userId: user?.id || null,
    refetch,
  });

  // Check if any seats are currently assigned
  const hasAssignedSeats = useMemo(() => {
    return draftRoster.some((entry) => entry.rosterId);
  }, [draftRoster]);

  // Load session's current team_id when modal opens (only when not already in team context)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setSelectedSeat(null);
      setSearchTerm('');
      setError(null);
      setSuccessMessage(null);
      setShowStartOverConfirm(false);
      setShowClearSeatsConfirm(false);
      setSelectedBoatId('');
      setAppliedBoatId(null);
      if (teamIdFromParent) {
        setSessionTeamId(teamIdFromParent);
        setSelectedTeamId(teamIdFromParent);
        setSessionTeamLoaded(true);
        // Load existing boat association even in team context
        telemetryService.getSessions().then((sessions) => {
          const session = sessions.find((s) => s.id === sessionId);
          if (session?.boat_id) {
            setSelectedBoatId(session.boat_id);
            setAppliedBoatId(session.boat_id);
          }
        });
      } else {
        setSessionTeamLoaded(false);
        telemetryService.getSessions().then((sessions) => {
          const session = sessions.find((s) => s.id === sessionId);
          const teamId = session?.team_id || null;
          setSessionTeamId(teamId);
          setSelectedTeamId(teamId || '');
          setSessionTeamLoaded(true);
          // Pre-select the existing boat if one is associated
          if (session?.boat_id) {
            setSelectedBoatId(session.boat_id);
            setAppliedBoatId(session.boat_id);
          }
        });
      }
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId, teamIdFromParent]); // setError and setSuccessMessage are stable setState functions

  // When there is exactly one non-demo team and no session/parent team lock, pre-select it (e.g. after create team).
  useEffect(() => {
    if (!isOpen) return;
    if (teamIdFromParent) return;
    if (!sessionTeamLoaded) return;
    if (sessionTeamId) return;
    if (isLoadingTeams) return;
    if (mappableTeams.length !== 1) return;
    const onlyId = mappableTeams[0].team.id;
    if (selectedTeamId !== onlyId) {
      setSelectedTeamId(onlyId);
    }
  }, [
    isOpen,
    teamIdFromParent,
    sessionTeamLoaded,
    sessionTeamId,
    isLoadingTeams,
    mappableTeams,
    selectedTeamId,
  ]);

  // Filter master roster based on search
  const filteredRoster = useMemo(() => {
    if (!searchTerm.trim()) return masterRoster;

    const term = searchTerm.toLowerCase();
    return masterRoster.filter(
      (rower) =>
        rower.firstName?.toLowerCase().includes(term) ||
        rower.lastName?.toLowerCase().includes(term) ||
        rower.profileId.toLowerCase().includes(term) ||
        rower.alias?.toLowerCase().includes(term) ||
        rower.preferredName?.toLowerCase().includes(term)
    );
  }, [masterRoster, searchTerm]);

  // Get seat assignment for a position from draft roster
  const getSeatAssignment = (position: number): SessionRosterEntry | null => {
    return draftRoster.find((entry) => entry.position === position) || null;
  };

  // Get display name for a roster entry
  const getDisplayName = (entry: SessionRosterEntry | null): string => {
    if (!entry || !entry.rosterId) return 'Unassigned';
    return (
      entry.displayName || `${entry.firstName || ''} ${entry.lastName || ''}`.trim() || 'Unassigned'
    );
  };

  // Build seat data for the shared hull component
  const seatData: SeatData[] = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 7, 8].map((position) => {
      const assignment = getSeatAssignment(position);
      const isAssigned = assignment?.rosterId !== null && assignment?.rosterId !== '';
      return {
        position,
        label: `Seat ${position}`,
        isAssigned: !!isAssigned,
        displayName: isAssigned ? getDisplayName(assignment) : 'Empty',
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRoster]);

  // Handle assigning a rower to a seat
  const handleAssignRower = (rower: CoachRosterRower) => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    if (!selectedSeat) return;

    const option = rosterOptionsMap.get(rower.profileId);
    if (!option) return;

    // Check for duplicate assignment
    const isAlreadyAssigned = draftRoster.some(
      (entry) => entry.position !== selectedSeat && entry.rosterId === option.profileId
    );

    if (isAlreadyAssigned) {
      const assignedSeat = draftRoster.find(
        (entry) => entry.position !== selectedSeat && entry.rosterId === option.profileId
      );
      const seatLabel = assignedSeat ? `Seat ${assignedSeat.position}` : 'another seat';
      setError(
        `This rower is already assigned to ${seatLabel}. Choose a different rower or unassign them first.`
      );
      return;
    }

    updateRosterEntry(selectedSeat, option);
    setSelectedSeat(null);
    setSearchTerm('');
    setError(null);
  };

  // Handle unassigning a rower from a seat
  const handleUnassign = async (position: number) => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    await unassignRower(position);
    setSelectedSeat(null);
    setSearchTerm('');
  };

  // Apply boat lineup to all seats
  const handleApplyBoatLineup = async () => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    if (!selectedBoatId || boatRoster.length === 0) return;

    setApplyingBoat(true);
    setError(null);

    try {
      let appliedCount = 0;

      for (const entry of boatRoster) {
        // Find the rower in the master roster options
        const option = rosterOptionsMap.get(entry.rower_id);
        if (option && entry.position >= 1 && entry.position <= 8) {
          updateRosterEntry(entry.position, option);
          appliedCount++;
        }
      }

      if (appliedCount > 0) {
        setAppliedBoatId(selectedBoatId);
        setSuccessMessage(
          `Applied ${appliedCount} seat${appliedCount > 1 ? 's' : ''} from boat lineup. Click Save to confirm.`
        );
      } else {
        setError('No matching rowers found in the boat lineup.');
      }

      setSelectedBoatId('');
      setSelectedSeat(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply boat lineup');
    } finally {
      setApplyingBoat(false);
    }
  };

  // Clear seats only: unassign all rowers but keep team association
  const handleClearSeatsConfirm = async () => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    if (!user?.id) return;
    setClearSeatsLoading(true);
    setError(null);
    try {
      const unassignChanges: SessionRosterChange[] = sessionRoster
        .filter((entry) => entry.id && entry.rosterId && entry.seatId)
        .map((entry) => ({
          rowerId: entry.id,
          seatPosition: entry.position,
          newRosterId: null,
          newSeatId: entry.seatId as string,
          newDisplayName: entry.seatId,
          newFirstName: null,
          newLastName: null,
          previousRosterId: entry.rosterId,
          previousSeatId: entry.seatId,
          previousDisplayName: entry.displayName,
        }));

      if (unassignChanges.length > 0) {
        await telemetryService.updateSessionRoster(sessionId, user.id, unassignChanges);
      }

      await refetch();
      setShowClearSeatsConfirm(false);
      setSuccessMessage('All seats cleared. You can now re-map rowers.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear seats. Please try again.');
    } finally {
      setClearSeatsLoading(false);
    }
  };

  // Remove from team: unassign session from team and clear all seat assignments
  const handleStartOverConfirm = async () => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    if (!user?.id) return;
    setStartOverLoading(true);
    setError(null);
    try {
      const unassignChanges: SessionRosterChange[] = sessionRoster
        .filter((entry) => entry.id && entry.rosterId && entry.seatId)
        .map((entry) => ({
          rowerId: entry.id,
          seatPosition: entry.position,
          newRosterId: null,
          newSeatId: entry.seatId as string,
          newDisplayName: entry.seatId,
          newFirstName: null,
          newLastName: null,
          previousRosterId: entry.rosterId,
          previousSeatId: entry.seatId,
          previousDisplayName: entry.displayName,
        }));

      if (unassignChanges.length > 0) {
        await telemetryService.updateSessionRoster(sessionId, user.id, unassignChanges);
      }
      const { error: teamError } = await telemetryService.updateSessionTeam(sessionId, null);
      if (teamError) throw teamError;

      // Clear boat association since boats belong to teams
      await telemetryService.updateSessionBoat(sessionId, null);

      // Invalidate team-sessions cache so session is removed from the team list
      await queryClient.invalidateQueries({ queryKey: ['team-sessions'] });

      // Close the modal and trigger parent refresh
      onSave?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to unassign session. Please try again.'
      );
    } finally {
      setStartOverLoading(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      return;
    }

    // Validate team selection
    if (!effectiveTeamId) {
      setError('Please select a team before saving roster mappings.');
      return;
    }

    // Update session's team_id FIRST if it changed (API requires team association)
    if (effectiveTeamId !== sessionTeamId) {
      try {
        const { error: updateError } = await telemetryService.updateSessionTeam(
          sessionId,
          effectiveTeamId
        );
        if (updateError) {
          setError('Failed to associate session with team. Please try again.');
          console.error('Failed to update session team:', updateError);
          return;
        }
        setSessionTeamId(effectiveTeamId);
        // Invalidate team-sessions cache so session appears in the team immediately
        await queryClient.invalidateQueries({ queryKey: ['team-sessions'] });
      } catch (err) {
        setError('Failed to associate session with team. Please try again.');
        console.error('Failed to update session team:', err);
        return;
      }
    }

    // Save boat_id if a boat lineup was applied
    if (appliedBoatId) {
      const { error: boatError } = await telemetryService.updateSessionBoat(
        sessionId,
        appliedBoatId
      );
      if (boatError) {
        console.warn('Failed to save boat association (non-fatal):', boatError);
      }
    }

    // Now save the roster changes
    const success = await saveChanges();
    if (success) {
      onSave?.();
      // Close modal on successful save
      onClose();
    }
    // Keep modal on error so user can see the error message
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 pt-6 border-b border-rowing-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-rowing-navy">Roster Mapping</h2>
                <p className="text-sm text-rowing-gray-600 mt-1">
                  {sessionName || 'Session'} - Assign rowers to seats
                </p>
                {isReadOnlyDemo && (
                  <p className="text-xs text-amber-700 mt-1">Demo session is read-only.</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-rowing-gray-400 hover:text-rowing-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex flex-col p-4 px-6 gap-6">
          {/* Team: loading (master), locked (mapping started or team context), or dropdown (no mapping yet) */}
          <div className="flex flex-col gap-2">
            {teamIdFromParent ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Team</span>
                    <p className="text-sm text-gray-900">
                      {coachTeams.find((m) => m?.team?.id === teamIdFromParent)?.team?.name ??
                        'This team'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isReadOnlyDemo && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowClearSeatsConfirm(true)}
                          disabled={saving || !hasAssignedSeats}
                          className="text-rowing-gray-600 hover:text-rowing-navy text-xs disabled:opacity-50"
                          title={!hasAssignedSeats ? 'No seats are assigned' : undefined}
                        >
                          Clear seats
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowStartOverConfirm(true)}
                          disabled={saving}
                          className="text-red-600 hover:text-red-700 text-xs"
                        >
                          Remove from team
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : !sessionTeamLoaded ? (
              <div className="text-sm text-gray-500">Loading session…</div>
            ) : sessionTeamId ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Team</span>
                    <p className="text-sm text-gray-900">
                      {coachTeams.find((m) => m?.team?.id === sessionTeamId)?.team?.name ??
                        'This team'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isReadOnlyDemo && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowClearSeatsConfirm(true)}
                          disabled={saving || !hasAssignedSeats}
                          className="text-rowing-gray-600 hover:text-rowing-navy text-xs disabled:opacity-50"
                          title={!hasAssignedSeats ? 'No seats are assigned' : undefined}
                        >
                          Clear seats
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowStartOverConfirm(true)}
                          disabled={saving}
                          className="text-red-600 hover:text-red-700 text-xs"
                        >
                          Remove from team
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {!isReadOnlyDemo && (
                  <p className="text-xs text-gray-500">
                    Session is mapped to this team.
                    {hasAssignedSeats
                      ? ' Use "Clear seats" to re-map, or "Remove from team" to fully unassign.'
                      : ' Use "Remove from team" to unassign from this team.'}
                  </p>
                )}
              </>
            ) : isLoadingTeams ? (
              <div className="text-sm text-gray-500">Loading teams…</div>
            ) : mappableTeams.length === 0 ? (
              <CreateTeamPrompt
                compact
                title="Create a team to map your roster"
                description="Roster mapping uses your team roster so you can assign athletes to seats. Create your first team — it will appear here so you can select it and map seats."
              />
            ) : (
              <>
                <label className="text-sm font-medium text-gray-700" htmlFor="roster-team-select">
                  Select team to map roster
                </label>
                <select
                  id="roster-team-select"
                  value={selectedTeamId}
                  onChange={(e) => {
                    setSelectedTeamId(e.target.value);
                    setError(null);
                  }}
                  className="h-9 w-full sm:w-auto min-w-[14rem] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus-visible:ring-accent/50 focus:border-transparent"
                  disabled={isLoadingTeams}
                >
                  <option value="">Choose a team…</option>
                  {mappableTeams.map((m) => (
                    <option key={m.team.id} value={m.team.id}>
                      {m.team.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Select a team to load its roster and map rowers to seats.
                </p>
              </>
            )}
          </div>

          {/* Quick Fill from Boat - Only show if team is selected and has boats */}
          {effectiveTeamId && teamBoats.length > 0 && (
            <div className="p-3 bg-accent/5 border border-accent/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Ship className="w-4 h-4 text-accent" />
                <span className="text-sm font-medium text-rowing-navy">Quick Fill from Boat</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedBoatId}
                  onChange={(e) => setSelectedBoatId(e.target.value)}
                  className="flex-1 h-9 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus-visible:ring-accent/50 focus:border-transparent"
                  disabled={isReadOnlyDemo || applyingBoat || saving}
                >
                  <option value="">Select a boat...</option>
                  {teamBoats.map((boat) => (
                    <option key={boat.id} value={boat.id}>
                      {boat.name} ({boat.boat_class}){boat.is_primary ? ' ★' : ''}
                    </option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApplyBoatLineup}
                  disabled={isReadOnlyDemo || !selectedBoatId || applyingBoat || saving}
                  loading={applyingBoat}
                >
                  Apply
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select a boat to auto-fill all seats with its saved lineup.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{successMessage}</p>
            </div>
          )}

          {/* Boat hull + roster selection - Only show if team is selected */}
          {effectiveTeamId && (
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Left: Boat visualization */}
              <div className="flex-shrink-0 self-center">
                <BoatHullVisualization
                  seats={seatData}
                  selectedSeat={selectedSeat}
                  onSeatClick={(pos) => setSelectedSeat(pos === -1 ? null : pos)}
                  onUnassign={handleUnassign}
                  disabled={saving || isReadOnlyDemo}
                />
              </div>

              {/* Right: Roster selection */}
              <div className="flex-1 min-w-0 border-t sm:border-t-0 pt-4 sm:pt-0 h-[50vh]">
                {selectedSeat ? (
                  <div className="flex flex-col h-full">
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Select Rower for Seat {selectedSeat}
                      </h3>
                      <div className="relative">
                        <Search
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                          size={18}
                        />
                        <input
                          type="text"
                          placeholder="Search rowers by name, seat ID, or alias..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus-visible:ring-accent/50 focus:border-accent"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar border border-gray-200 rounded-md">
                      {masterRosterLoading ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          Loading rowers...
                        </div>
                      ) : filteredRoster.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          {searchTerm
                            ? 'No rowers found matching your search.'
                            : 'No rowers available.'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {filteredRoster.map((rower) => {
                            const displayName =
                              rower.preferredName ||
                              rower.alias ||
                              `${rower.firstName || ''} ${rower.lastName || ''}`.trim() ||
                              'Unknown';
                            const fullName =
                              `${rower.firstName || ''} ${rower.lastName || ''}`.trim();

                            const option = rosterOptionsMap.get(rower.profileId);
                            const isAssignedElsewhere = option
                              ? draftRoster.some(
                                  (entry) =>
                                    entry.position !== selectedSeat &&
                                    entry.rosterId === option.profileId
                                )
                              : false;

                            return (
                              <button
                                key={rower.profileId}
                                onClick={() => handleAssignRower(rower)}
                                disabled={isAssignedElsewhere}
                                className={`w-full text-left px-4 py-2.5 transition-colors ${
                                  isAssignedElsewhere
                                    ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                    : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`font-medium text-sm ${rower.isDemo ? 'text-gray-600' : 'text-gray-900'}`}
                                  >
                                    {displayName}
                                  </div>
                                  {rower.isDemo && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">
                                      Demo
                                    </span>
                                  )}
                                </div>
                                {fullName && fullName !== displayName && (
                                  <div
                                    className={`text-xs mt-0.5 ${rower.isDemo ? 'text-gray-400' : 'text-gray-500'}`}
                                  >
                                    {fullName}
                                  </div>
                                )}
                                {isAssignedElsewhere && (
                                  <div className="text-xs text-yellow-600 mt-1">
                                    Already assigned to another seat
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400 italic">
                    Click a seat to assign a rower
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-3 px-6 border-t border-rowing-gray-200">
          <Button variant="ghost" onClick={onClose} size="sm" disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            size="sm"
            disabled={saving || isReadOnlyDemo || !effectiveTeamId}
            loading={saving}
            className="bg-accent hover:bg-accent-hover text-accent-foreground"
            title={
              isReadOnlyDemo
                ? 'Demo sessions are read-only.'
                : !effectiveTeamId
                  ? 'Select or create a team before saving.'
                  : undefined
            }
          >
            Save
          </Button>
        </div>
      </div>

      {/* Clear seats confirmation */}
      {showClearSeatsConfirm && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-sm mx-4 border border-gray-200">
            <p className="text-sm text-gray-700 mb-4">
              This will clear all seat assignments. The session will remain associated with{' '}
              <strong>
                {coachTeams.find((m) => m?.team?.id === effectiveTeamId)?.team?.name ?? 'this team'}
              </strong>
              .
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowClearSeatsConfirm(false)}
                disabled={clearSeatsLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleClearSeatsConfirm}
                disabled={clearSeatsLoading}
                loading={clearSeatsLoading}
              >
                Clear seats
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove from team confirmation */}
      {showStartOverConfirm && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-sm mx-4 border border-gray-200">
            <p className="text-sm text-gray-700 mb-4">
              This will remove this session from{' '}
              <strong>
                {coachTeams.find((m) => m?.team?.id === effectiveTeamId)?.team?.name ?? 'this team'}
              </strong>{' '}
              and clear all seat assignments. You can then select a different team.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowStartOverConfirm(false)}
                disabled={startOverLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleStartOverConfirm}
                disabled={startOverLoading}
                loading={startOverLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Remove from team
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RosterMappingModal;
